import React, { ChangeEvent, useState, type JSX } from 'react';
import styled from 'styled-components';
import Input from 'components/UI/Input';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FontWeight } from 'lib/styles';
import { Text } from 'styles/themeColors';
import * as Cons from 'data/Consignment';
import RentalDates from './components/RentalDates';
import DateView from 'components/UI/DateView';
import { CONSIGNMENT_TYPES } from 'constants/order';
import { formatDateTime } from 'utils/formatters';
import { useReadPermission } from 'hooks/useUserRoles';
import { PermissionCode } from 'data/UserPermissions';
import { ReadOnlyWrapper } from 'components/common/ReadOnlyWrapper';
import { DateTime } from 'luxon';

const LabelText = styled.span`
  display: block;
  font-weight: ${FontWeight.medium};
  text-align: left;
  margin-bottom: 5px;
  color: ${Text.primary};
  font-size: 15px;
`;
interface Props {
  warehouseName: string;
  referenceNumber: string;
  consignmentId: string;
  pickupDate?: string | null;
  onReferenceIdChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onPickupDateChange: (value: any) => void;
  button?: JSX.Element;
  isRental?: boolean;
  consignmentData?: Cons.ConsignmentDetails;
  setShowModal?: (show: boolean) => void;
};

const PickupDetails = ({
  warehouseName,
  referenceNumber,
  consignmentId,
  pickupDate,
  onReferenceIdChange,
  onPickupDateChange,
  button,
  isRental,
  consignmentData,
  setShowModal
}: Props) => {

  const { readOnly } = useReadPermission(PermissionCode.PICK_UP_DETAILS_MANAGEMENT);
  const [openDatePicker, setOpenDatePicker] = useState(false);

  return (
    <>
      <FlexboxContainer flexDirection="row" width="100%" justifyContent="space-between">
        <FlexboxContainer justifyContent="space-between" alignItems="center">
          <Input
            id="reference-id"
            labelName="Reference #"
            placeholder="Enter reference number"
            value={referenceNumber}
            onChange={onReferenceIdChange}
            width="272px"
            readOnly={readOnly}
          />
          <FlexboxContainer flexDirection="column" width='150px' margin='0 0 0 4px'>
            <LabelText>Ready for pickup</LabelText>
            {
              consignmentData?.consignmentType === CONSIGNMENT_TYPES.PURCHASE ?
                <ReadOnlyWrapper readOnly={readOnly}>
                  <DatePicker
                    value={pickupDate ? DateTime.fromISO(pickupDate) : null}
                    onChange={(value: DateTime | null) => {
                      if (value) {
                        onPickupDateChange(value.toISO() || '');
                      }
                    }}
                    open={openDatePicker}
                    onOpen={() => setOpenDatePicker(true)}
                    onClose={() => setOpenDatePicker(false)}
                    format="yyyy MMM dd"
                    slotProps={{
                      textField: {
                        size: 'small',
                        onClick: () => !readOnly && setOpenDatePicker(true),
                        InputProps: {
                          readOnly: true,
                          sx: { cursor: readOnly ? 'default' : 'pointer' }
                        }
                      }
                    }}
                    disabled={readOnly}
                  />
                </ReadOnlyWrapper> :
                <ReadOnlyWrapper readOnly={readOnly}>
                  <DateView date={formatDateTime(pickupDate!)} onClick={readOnly ? undefined : () => setShowModal?.(true)} />
                </ReadOnlyWrapper>
            }
          </FlexboxContainer>
        </FlexboxContainer>
        <FlexboxContainer margin="30px 0 0 4px" justifyContent="flex-end" alignItems="flex-end">
          {button}
        </FlexboxContainer>
      </FlexboxContainer>
      {isRental ? <RentalDates datesFormat='yyyy MMM dd' consignmentData={consignmentData} isRental={isRental} /> : ''}

    </>
  );
};

export default React.memo(PickupDetails);
